import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subscription, switchMap, timer } from 'rxjs';

import { FeedFileService } from '../services/feed-file.service';
import { FEED_FILE_MAX_BYTES, FeedFile, FileSourceType, FilesCapability } from '../models/provider.model';
import { acceptFor, requiredRoleStatus, roleDeclared } from '../util/file-rules.util';
import { roleLabel } from '../util/descriptor-text';
import { LanguageService } from '../../../core/i18n/language.service';
import { FeedFileDialogComponent } from './feed-file-dialog.component';
import { LabelBadge } from '../../../shared/components/label-badge/label-badge';
import { ToastService } from '../../../shared/services/toast-service';
import { ModalService } from '../../../shared/components/modal/modal.service';
import { formatBytes } from '../../../shared/utils/format-bytes';
import { relativeTime } from '../../../shared/utils/relative-time';
import { apiErrorMessage, openDownloadTab, statusErrorKey } from '../util/feed-file-upload';

/** How often a file is re-fetched while waiting for a check to land. */
const CHECK_POLL_MS = 3_000;
/** Give up waiting after this long (core checks within ~30 s). */
const CHECK_POLL_TIMEOUT_MS = 120_000;

/**
 * A file-based feed's named files (`FeedFileService`), each with its own
 * source — the last manual upload, or a URL core watches. Rows show the
 * filename, role, source, content size/age (or "no content yet") and, for URL
 * files, how the watching goes (OK with last/next check, or failing with the
 * error, since when and how many attempts). Actions: upload content (switches
 * a URL file to MANUAL — confirmed first, URL settings are kept), check now
 * (URL), download current content, edit (`FeedFileDialogComponent`), delete.
 *
 * After a check is requested — check-now, or saving a URL source — the file is
 * polled until `check.lastCheckAt` moves past that moment. `changed` fires
 * after any change that can produce a new file set, so the page can refresh
 * sets / health / the feed's files summary.
 *
 * `maxFiles` + `defaultFilename` + `quickStart` steer a feed to a fixed shape
 * — GTFS: exactly one zip, created on the first upload or URL.
 */
@Component({
  selector: 'app-feed-file-list',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge, FeedFileDialogComponent],
  templateUrl: './feed-file-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class FeedFileListComponent {
  private readonly feedFileService = inject(FeedFileService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly feedId = input.required<string>();
  /** Most files this feed should have; null = no limit. */
  readonly maxFiles = input<number | null>(null);
  /** Shown (already translated) when the feed has more files than `maxFiles`. */
  readonly overLimitWarning = input('');
  /** Prefilled filename for new files (e.g. `gtfs.zip`). */
  readonly defaultFilename = input('');
  /** `accept` for upload pickers (e.g. `.zip,application/zip`), when the provider's descriptor
   *  doesn't list content types for a file's role. */
  readonly accept = input('');
  /** The provider's file rules (descriptor `capabilities.files`); null = none known. */
  readonly filesCapability = input<FilesCapability | null>(null);
  /** For role label translation keys. */
  readonly providerType = input<string | null>(null);
  /** Empty state offers "Upload {defaultFilename}" / "Use a URL" instead of a
   *  generic Add file — a quick upload creates the file with `defaultFilename`. */
  readonly quickStart = input(false);

  readonly changed = output<void>();

  readonly files = signal<FeedFile[]>([]);
  readonly isLoading = signal(true);
  /** File id being uploaded to ('new' during a quick-start create + upload). */
  readonly uploadingId = signal<string | null>(null);
  readonly downloadingId = signal<string | null>(null);
  /** Files waiting for a requested check to land. */
  readonly checkingIds = signal<ReadonlySet<string>>(new Set());
  /** Open dialog: the file to edit (null = create) and, on create, the preselected source. */
  readonly dialog = signal<{ file: FeedFile | null; source: FileSourceType } | null>(null);

  readonly formatBytes = formatBytes;
  readonly relativeTime = relativeTime;

  readonly canAdd = computed(() => {
    const max = this.maxFiles();
    return max == null || this.files().length < max;
  });
  readonly overLimit = computed(() => {
    const max = this.maxFiles();
    return max != null && this.files().length > max;
  });
  /** Core holds file sets back until every feed file has content. */
  readonly missingContent = computed(() => this.files().filter((f) => !f.content).length);
  /** Required roles and whether each has a file with content — core also holds sets back until all are. */
  readonly requiredRoles = computed(() => {
    this.languageService.current();
    return requiredRoleStatus(this.filesCapability(), this.files()).map((r) => ({
      ...r,
      label: roleLabel(this.transloco, this.providerType(), r.name),
    }));
  });
  /** The shared picker's `accept`, set for the file being uploaded to. */
  readonly pickerAccept = signal('');

  private readonly picker = viewChild<ElementRef<HTMLInputElement>>('picker');
  /** What the shared hidden picker uploads to: a file id, or 'quick' (create first). */
  private pickTarget: string | 'quick' | null = null;
  private readonly checkPolls = new Map<string, Subscription>();

  private readonly loadOnFeed = effect(() => {
    this.feedId();
    untracked(() => this.fetch());
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.checkPolls.forEach((sub) => sub.unsubscribe()));
  }

  /** A file whose role the provider doesn't declare (core won't deliver sets until it's fixed). */
  roleUndeclared(file: FeedFile): boolean {
    return !roleDeclared(this.filesCapability(), file.role);
  }

  /** A role's display label. */
  roleText(role: string): string {
    return roleLabel(this.transloco, this.providerType(), role);
  }

  isChecking(file: FeedFile): boolean {
    return this.checkingIds().has(file.id);
  }

  // ── Dialog ────────────────────────────────────────────────────────────

  openCreate(source: FileSourceType = 'MANUAL'): void {
    this.dialog.set({ file: null, source });
  }

  openEdit(file: FeedFile): void {
    this.dialog.set({ file, source: file.sourceType });
  }

  onSaved(file: FeedFile): void {
    this.upsert(file);
    this.changed.emit();
    // Saving a URL source makes core check it within ~30 s — follow it.
    if (file.sourceType === 'URL') this.watchCheck(file.id, Date.now());
  }

  // ── Upload ────────────────────────────────────────────────────────────

  /** Uploading to a URL file switches it to MANUAL (its URL settings are kept) — confirm first. */
  async upload(file: FeedFile): Promise<void> {
    if (file.sourceType === 'URL') {
      const confirmed = await this.modal.confirm({
        kind: 'warning',
        title: this.transloco.translate('providers.feedFiles.list.replaceUrl.title'),
        message: this.transloco.translate('providers.feedFiles.list.replaceUrl.message', { filename: file.filename }),
        confirmLabel: this.transloco.translate('providers.feedFiles.list.replaceUrl.confirm'),
      });
      if (!confirmed) return;
    }
    this.openPicker(file.id);
  }

  quickUpload(): void {
    this.openPicker('quick');
  }

  onPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = input.files?.[0] ?? null;
    input.value = '';
    const target = this.pickTarget;
    this.pickTarget = null;
    if (!picked || !target) return;
    if (picked.size > FEED_FILE_MAX_BYTES) {
      this.toastService.show(this.transloco.translate('providers.feedFiles.errors.tooLarge', { max: formatBytes(FEED_FILE_MAX_BYTES) }), 'error');
      return;
    }
    if (target === 'quick') this.createAndUpload(picked);
    else this.uploadTo(target, picked);
  }

  // ── Check / download / delete ─────────────────────────────────────────

  checkNow(file: FeedFile): void {
    const clickedAt = Date.now();
    this.markChecking(file.id, true);
    this.feedFileService
      .checkNow(this.feedId(), file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.upsert(res.data);
          this.watchCheck(file.id, clickedAt);
        },
        error: (err: HttpErrorResponse) => {
          this.markChecking(file.id, false);
          this.showError(err);
        },
      });
  }

  download(file: FeedFile): void {
    const tab = openDownloadTab();
    this.downloadingId.set(file.id);
    this.feedFileService
      .getDownloadUrl(this.feedId(), file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.downloadingId.set(null);
          tab.go(res.data.url);
        },
        error: (err: HttpErrorResponse) => {
          this.downloadingId.set(null);
          tab.cancel();
          this.showError(err, 'providers.feedFiles.toast.downloadFailed');
        },
      });
  }

  async remove(file: FeedFile): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('providers.feedFiles.list.deleteConfirm.title'),
      message: this.transloco.translate('providers.feedFiles.list.deleteConfirm.message', { filename: file.filename }),
      confirmLabel: this.transloco.translate('providers.feedFiles.list.deleteConfirm.confirm'),
    });
    if (!confirmed) return;
    this.feedFileService
      .delete(this.feedId(), file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.files.update((files) => files.filter((f) => f.id !== file.id));
          this.toastService.show(this.transloco.translate('providers.feedFiles.toast.deleted'), 'success');
          this.changed.emit();
        },
        error: (err: HttpErrorResponse) => this.showError(err),
      });
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private openPicker(target: string | 'quick'): void {
    this.pickTarget = target;
    const role = this.files().find((f) => f.id === target)?.role ?? null;
    this.pickerAccept.set(acceptFor(this.filesCapability(), role, this.accept()));
    // Apply the new accept before the click opens the dialog.
    const picker = this.picker()?.nativeElement;
    if (picker) picker.accept = this.pickerAccept();
    picker?.click();
  }

  private uploadTo(fileId: string, content: File): void {
    this.uploadingId.set(fileId);
    this.feedFileService
      .uploadContent(this.feedId(), fileId, content)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.uploadingId.set(null);
          this.upsert(res.data);
          this.toastService.show(this.transloco.translate('providers.feedFiles.toast.uploaded'), 'success');
          this.changed.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.uploadingId.set(null);
          this.showError(err);
        },
      });
  }

  /** Quick start: create the feed's file (MANUAL, `defaultFilename`), then upload into it. */
  private createAndUpload(content: File): void {
    this.uploadingId.set('new');
    this.feedFileService
      .create(this.feedId(), { filename: this.defaultFilename() || content.name, sourceType: 'MANUAL' })
      .pipe(
        switchMap((res) => {
          this.upsert(res.data);
          return this.feedFileService.uploadContent(this.feedId(), res.data.id, content);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.uploadingId.set(null);
          this.upsert(res.data);
          this.toastService.show(this.transloco.translate('providers.feedFiles.toast.uploaded'), 'success');
          this.changed.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.uploadingId.set(null);
          this.showError(err);
          this.changed.emit(); // the file may have been created even if the upload failed
        },
      });
  }

  /** Poll the file until its last check is newer than `since` (or give up). */
  private watchCheck(fileId: string, since: number): void {
    this.checkPolls.get(fileId)?.unsubscribe();
    this.markChecking(fileId, true);
    const started = Date.now();
    const sub = timer(CHECK_POLL_MS, CHECK_POLL_MS)
      .pipe(
        switchMap(() => this.feedFileService.get(this.feedId(), fileId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const file = res.data;
          this.upsert(file);
          const lastCheck = file.check?.lastCheckAt ? new Date(file.check.lastCheckAt).getTime() : 0;
          const done = file.sourceType !== 'URL' || lastCheck >= since || Date.now() - started > CHECK_POLL_TIMEOUT_MS;
          if (!done) return;
          this.stopWatch(fileId);
          if (file.check?.lastResult === 'NEW_CONTENT') this.changed.emit();
        },
        error: () => this.stopWatch(fileId),
      });
    this.checkPolls.set(fileId, sub);
  }

  private stopWatch(fileId: string): void {
    this.checkPolls.get(fileId)?.unsubscribe();
    this.checkPolls.delete(fileId);
    this.markChecking(fileId, false);
  }

  private markChecking(fileId: string, checking: boolean): void {
    this.checkingIds.update((current) => {
      const next = new Set(current);
      if (checking) next.add(fileId);
      else next.delete(fileId);
      return next;
    });
  }

  private upsert(file: FeedFile): void {
    this.files.update((files) => {
      const i = files.findIndex((f) => f.id === file.id);
      const next = i >= 0 ? files.map((f, j) => (j === i ? file : f)) : [...files, file];
      return next.sort((a, b) => a.filename.localeCompare(b.filename));
    });
  }

  /** Core's message when it sent one, else status-based text. */
  private showError(err: HttpErrorResponse, fallbackKey?: string): void {
    const message =
      apiErrorMessage(err) ??
      this.transloco.translate<string>(fallbackKey ?? `providers.feedFiles.errors.${statusErrorKey(err.status)}`, {
        max: formatBytes(FEED_FILE_MAX_BYTES),
      });
    this.toastService.show(message, 'error');
  }

  private fetch(): void {
    this.isLoading.set(true);
    this.feedFileService
      .getAll(this.feedId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.files.set(res.data ?? []);
          this.isLoading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.showError(err, 'providers.feedFiles.toast.loadFailed');
        },
      });
  }
}
